import { Sparkles, Mic, Music2, Headphones, Rocket } from "lucide-react";

const STEPS = [
    {
        n: "01",
        icon: Sparkles,
        title: "Discover your sound",
        body: "We start with your favourite artists, vocal strengths and creative goals. Pop, indie, rap, country, rock, EDM, metal, soul — whatever your sound choices and lyrical ideas, we build around you.",
        accent: "#C2410C",
    },
    {
        n: "02",
        icon: Music2,
        title: "Create your song",
        body: [
            "Whether you want to create something completely original or record your own interpretation of a favourite song, we\u2019ll help bring it to life.",
            "From songwriting and creative development to custom music, arrangement and professional production, we\u2019ll work with you to create a finished track that feels personal, polished and genuinely yours.",
        ],
        accent: "#D4A017",
    },
    {
        n: "03",
        icon: Mic,
        title: "Train your voice",
        body: "Work 1:1 with vocal coaches to build confidence, strengthen your singing, learn recording technique and unlock the voice that's going to make your new track, uniquely yours.",
        accent: "#1B4D3E",
    },
    {
        n: "04",
        icon: Headphones,
        title: "Record like a real artist",
        body: "Record your vocals with your vocal coach guiding you. Then your new track is sent for final production, mixing and mastering until your single sounds release‑ready.",
        accent: "#7C2D12",
    },
    {
        n: "05",
        icon: Rocket,
        title: "Launch worldwide",
        body: [
            "Spotify. Apple Music. TikTok. YouTube Music. Amazon Music \u2014 and the My First Single Radio Station, where tracks created by My First Single artists can be heard together in one dedicated place.",
            "With professional artwork, branding, social launch content and digital distribution, your music doesn\u2019t just get recorded \u2014 it gets released.",
            "You become a real released artist.",
        ],
        accent: "#C2410C",
    },
];

export default function HowItWorks() {
    return (
        <section
            id="how-it-works"
            className="relative px-6 md:px-10 py-16 md:py-24 bg-[#0A0A0A] text-white overflow-hidden border-b-4 border-black"
            data-testid="how-it-works-section"
        >
            <div className="absolute top-1/4 -right-32 w-[400px] h-[400px] rounded-full bg-[#C2410C] opacity-20 blur-3xl" aria-hidden />
            <div className="absolute bottom-1/4 -left-32 w-[400px] h-[400px] rounded-full bg-[#D4A017] opacity-15 blur-3xl" aria-hidden />

            <div className="relative max-w-7xl mx-auto">
                <div className="reveal max-w-3xl mb-10 md:mb-14">
                    <h2 className="font-display tracking-tighter font-black leading-[0.95] text-5xl md:text-6xl lg:text-7xl">
                        Your journey from
                        <br />
                        beginner to <em className="not-italic bg-[#D4A017] text-black px-3 -rotate-1 inline-block">released</em>.
                    </h2>
                    <p className="mt-6 text-white/70 text-lg max-w-xl">
                        Five steps. One real, finished song with your name on it.
                    </p>
                </div>

                <ol className="relative border-l-2 border-dashed border-white/30 ml-2 md:ml-6 space-y-14 md:space-y-20">
                    {STEPS.map(({ n, icon: Icon, title, body, accent }, idx) => (
                        <li
                            key={n}
                            className="relative pl-8 md:pl-14 reveal"
                            style={{ transitionDelay: `${idx * 60}ms` }}
                        >
                            <span
                                className="absolute -left-[10px] top-2 w-5 h-5 rounded-full ring-4 ring-[#0A0A0A] border-2 border-black"
                                style={{ background: accent }}
                            />
                            <div className="grid md:grid-cols-12 gap-6 md:gap-12 items-start">
                                <div className="md:col-span-3 flex items-center gap-4">
                                    <span className="font-mono text-xs font-bold" style={{ color: accent }}>{n}</span>
                                    <span
                                        className="w-12 h-12 rounded-2xl flex items-center justify-center border-2 border-black text-black"
                                        style={{ background: accent }}
                                    >
                                        <Icon size={20} />
                                    </span>
                                </div>
                                <div className="md:col-span-9">
                                    <h3 className="font-display font-bold tracking-tight text-2xl md:text-4xl text-white">
                                        {title}
                                    </h3>
                                    <div className="mt-3 space-y-3 text-white/70 text-base md:text-lg max-w-2xl leading-relaxed">
                                        {Array.isArray(body)
                                            ? body.map((para, i) => <p key={i}>{para}</p>)
                                            : <p>{body}</p>}
                                    </div>
                                </div>
                            </div>
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    );
}
